/*
 * Lemonade Training Oy copy rights all right reserved.
 * Schedule Template Callable Contracts
 *
 * Request and response shapes of applyScheduleTemplate, duplicateWeek ("Repeat a
 * week") and undoScheduleTemplateApply. The template and slot documents live in
 * box.ts; this file holds only what crosses the callable seam.
 */

/** Most target weeks one apply or repeat may fill in a single call. */
export const SCHEDULE_TEMPLATE_MAX_TARGET_WEEKS = 8;

/** One class the apply WOULD create (dry-run) or DID create (live). */
export type ApplyCreatedEntry = {
  classID: string;
  weekIndex: number;
  dayOfWeek: number;
  startISO: string;
  classTypeID: string;
  /** null = No room. Absent only from an older deployed function, read like null. */
  roomID?: string | null;
  /** The target week this class landed in; present only on a `targetWeekStartISOs` call. */
  weekStartISO?: string;
};

/**
 * OPEN enum: the server may add reasons without a client change, so a consumer
 * must render an unknown value gracefully. Known today: collision_existing_class |
 * collision_booked_class | dst_gap | inactive_class_type |
 * already_created_by_this_apply | past_slot.
 */
export type ApplySkipReason = string;

export type ApplySkippedEntry = {
  weekIndex: number;
  dayOfWeek: number;
  startISO: string | null;
  classTypeID: string;
  reason: ApplySkipReason;
  /** Present only on collision_booked_class. */
  bookingCount?: number;
  waitlistCount?: number;
  weekStartISO?: string;
};

/** A created class that landed without a coach. Reasons: coach_left_box | coach_not_assigned. */
export type ApplyCoachlessEntry = {
  classID: string;
  weekIndex?: number;
  slotCoachUID: string | null;
  reason: string;
};

/** Sanitized reasons: invalid_slot | duplicate_slot | batch_commit_failed | create_failed. */
export type ApplyFailedEntry = {
  weekIndex: number;
  dayOfWeek: number;
  startISO: string | null;
  classTypeID: string;
  reason: string;
  weekStartISO?: string;
};

/** Per-target-week breakdown of a `targetWeekStartISOs` call. */
export type ApplyReceiptWeek = {
  /** Echo of the request's entry for this week. */
  weekStartISO: string;
  /** Which template week filled it: targets rotate A, B, A, B from week A. Always 0 for a repeat. */
  templateWeekIndex: number;
  created: number;
  skipped: number;
  failed: number;
};

export type ApplyReceiptCounts = {
  requested: number;
  created: number;
  skipped: number;
  coachless: number;
  failed: number;
  /** Created classes whose coach came from a coachOverride; 0 for a repeat. */
  overriddenCoachCount: number;
  /**
   * Created classes whose consulted room is not a room of this gym and so landed as
   * No room. Absent from an older deployed function; read it as 0.
   */
  roomFallbackCount?: number;
  /**
   * duplicateWeek only: source classes left out because they are cancelled.
   * Counted once per source class, not once per target week.
   */
  cancelledNotCopied?: number;
};

/**
 * The receipt of applyScheduleTemplate and duplicateWeek, live or dry-run. One call
 * returns ONE receipt and ONE applyToken, however many target weeks it covered.
 */
export type ApplyScheduleTemplateResult = {
  success: boolean;
  mode: "apply" | "dry_run";
  /** null for a repeated week (no saved template behind it). */
  templateID: string | null;
  templateName: string | null;
  /** The single-week anchor, or the FIRST of `targetWeekStartISOs`. */
  targetWeekStartISO: string;
  /** Echo of the request's list; absent on a single-week call. */
  targetWeekStartISOs?: string[];
  weekCount: number;
  counts: ApplyReceiptCounts;
  created: ApplyCreatedEntry[];
  skipped: ApplySkippedEntry[];
  coachless: ApplyCoachlessEntry[];
  failed: ApplyFailedEntry[];
  /** Stamped on every created class; pass it to undoScheduleTemplateApply. */
  applyToken: string;
  /** Present only on a `targetWeekStartISOs` call, one entry per target, in order. */
  weeks?: ApplyReceiptWeek[];
};

/**
 * One apply-time coach choice, addressing a template slot by its full identity.
 * coachUID null = apply that slot coachless. With several target weeks it applies
 * to every week that uses the slot's template week.
 */
export type CoachOverride = {
  weekIndex: number;
  dayOfWeek: number;
  startTime: string;
  classTypeID: string;
  coachUID: string | null;
};

/**
 * Send EXACTLY ONE of `targetWeekStartISO` (legacy: the whole template from that
 * Monday, weekCount weeks) or `targetWeekStartISOs` (1 to 8 distinct, ascending,
 * not-past box-local Mondays; each gets ONE template week, rotating A, B, A, B).
 * Both present is invalid-argument.
 */
export type ApplyScheduleTemplateRequest = {
  boxID: string;
  templateID: string;
  targetWeekStartISO?: string;
  targetWeekStartISOs?: string[];
  dryRun?: boolean;
  /** 60 s double-submit cache key only. */
  idempotencyToken?: string;
  /** Provenance token; pass the first pass's on a "Finish applying" retry. ≤128 chars. */
  applyToken?: string;
  coachOverrides?: CoachOverride[];
};

export type ApplyScheduleTemplateResponse = ApplyScheduleTemplateResult;

/**
 * "Repeat a week". At most one of `targetWeekStartISO` / `targetWeekStartISOs`;
 * with neither, the target is the week after the source.
 */
export type DuplicateWeekRequest = {
  boxID: string;
  sourceWeekStartISO: string;
  targetWeekStartISO?: string;
  targetWeekStartISOs?: string[];
  /** Copy only these source-week classes; absent = the whole week. */
  classIDs?: string[];
  dryRun?: boolean;
  idempotencyToken?: string;
  /** Same meaning as on ApplyScheduleTemplateRequest. */
  applyToken?: string;
};

export type DuplicateWeekResponse = ApplyScheduleTemplateResult;

/**
 * Without `createdClassIDs` the server finds the classes by `applyToken` (the
 * form a repeat of several weeks needs). With it, only those ids are candidates
 * (≤ SCHEDULE_TEMPLATE_MAX_SLOTS). Either way it deletes only classes of that apply
 * with no bookings, no waitlist and not cancelled; a coach only their own apply.
 */
export type UndoScheduleTemplateApplyRequest = {
  boxID: string;
  applyToken: string;
  createdClassIDs?: string[];
};

/**
 * OPEN enum reason: has_bookings | cancelled | provenance_mismatch |
 * not_your_apply | already_gone.
 */
export type UndoKeptEntry = {
  classID: string;
  reason: string;
};

export type UndoScheduleTemplateApplyCounts = {
  requested: number;
  undone: number;
  kept: number;
  keptBooked: number;
};

export type UndoScheduleTemplateApplyResult = {
  success: boolean;
  undone: number;
  kept: UndoKeptEntry[];
  counts: UndoScheduleTemplateApplyCounts;
};
